import { morph } from '@theme/morph';
import { ThemeEvents } from '@theme/events';
import { yieldToMainThread } from '@theme/utilities';

/**
 * Custom element for option gallery links (thumbs/pills) that loads the target product
 * via fetch and updates the page DOM by morphing <main>, matching variant-picker.js
 * behavior when switching to a different product (no full page redirect).
 */
class AriOptionGalleryLink extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keydown', this.#handleKeydown);
    this.setAttribute('role', 'link');
    if (!this.hasAttribute('tabindex')) {
      this.setAttribute('tabindex', '0');
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeydown);
    this.#abortController?.abort();
  }

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    if (event.defaultPrevented) return;
    const href = this.getAttribute('href');
    if (!href || href === '#' || href === '') return;
    event.preventDefault();
    this.#navigate(href);
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    if (event.defaultPrevented) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const href = this.getAttribute('href');
    if (!href || href === '#' || href === '') return;
    event.preventDefault();
    this.#navigate(href);
  };

  /**
   * Fetches the URL and updates the page by morphing main (same as variant-picker updateMain).
   * @param {string} url
   */
  async #navigate(url) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#abortController.signal });
      if (!response.ok) {
        window.location.href = url;
        return;
      }
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      // Align with variant-picker: remove defer from overflow-list in response
      html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

      const main = document.querySelector('main');
      const newMain = html.querySelector('main');
      if (!main || !newMain) {
        window.location.href = url;
        return;
      }

      morph(main, newMain);

      await yieldToMainThread();
      history.pushState({}, '', url);

      const el = document.querySelector('tab-accordion');

      if(el) el.dispatchEvent(new CustomEvent('refresh-tabs'));

      const domUpdated = new CustomEvent(ThemeEvents.variantDomUpdated, { bubbles: true });
      const mainEl = document.querySelector('main');
      if (mainEl) {
        mainEl.dispatchEvent(domUpdated);
      } else {
        document.dispatchEvent(domUpdated);
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('[ari-option-gallery-link]', error);
      window.location.href = url;
    }
  }
}

if (!customElements.get('ari-option-gallery-link')) {
  customElements.define('ari-option-gallery-link', AriOptionGalleryLink);
}
